import React, { useState, useEffect } from 'react'
import {
  Card,
  Button,
  Space,
  Typography,
  Input,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Tag,
  Badge,
  List,
  Divider,
  Tooltip,
  FloatButton,
  Grid,
  Empty,
  Spin,
  Pagination,
  Drawer,
  Radio
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PushpinOutlined,
  PushpinFilled,
  FileTextOutlined,
  TagOutlined,
  FilterOutlined,
  EyeOutlined,
  BookOutlined,
  FlagOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import notesService from '../../services/notesService'

const { Title, Text, Paragraph } = Typography
const { Search } = Input
const { TextArea } = Input
const { Option } = Select
const { useBreakpoint } = Grid

const NotesPage = () => {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [viewingNote, setViewingNote] = useState(null)
  const [form] = Form.useForm()

  // Filter states
  const [searchTerm, setSearchTerm] = useState('') // สำหรับ API call
  const [searchInput, setSearchInput] = useState('') // สำหรับ input display
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedPriority, setSelectedPriority] = useState('')
  const [showPinnedOnly, setShowPinnedOnly] = useState('')
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [searchKey, setSearchKey] = useState(0)

  // Data states
  const [categories, setCategories] = useState([])
  const [defaultCategories, setDefaultCategories] = useState([])
  const [priorityCounts, setPriorityCounts] = useState([])

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  })

  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Priority configurations
  const priorityConfig = {
    low: { color: 'blue', label: 'ต่ำ', icon: '🔵' },
    medium: { color: 'orange', label: 'ปานกลาง', icon: '🟡' },
    high: { color: 'red', label: 'สูง', icon: '🔴' }
  }

  // Default categories list
  const defaultCategoriesList = [
    'general', 'งาน', 'โครงการ', 'ความคิด', 'การประชุม', 'ลูกค้า', 'ผู้ขาย'
  ]

  useEffect(() => {
    fetchNotes({})
    fetchCategories()
  }, [])

  // *** ใช้แพทเทิร์น React State Timing Issue Fix ***
  const fetchNotes = async (customParams = {}) => {
    setLoading(true)
    try {
      // ใช้ค่าใหม่ถ้ามี ไม่งั้นใช้ค่าจาก state
      const searchParam = customParams.search !== undefined ? customParams.search : searchTerm
      const categoryParam = customParams.category !== undefined ? customParams.category : selectedCategory
      const priorityParam = customParams.priority !== undefined ? customParams.priority : selectedPriority
      const pinnedParam = customParams.pinned !== undefined ? customParams.pinned : showPinnedOnly
      const pageParam = customParams.page !== undefined ? customParams.page : pagination.page
      const limitParam = customParams.limit !== undefined ? customParams.limit : pagination.limit

      console.log('🔍 FETCH NOTES PARAMS:', {
        searchParam,
        categoryParam,
        priorityParam,
        pinnedParam,
        pageParam,
        limitParam,
        customParams
      })

      const apiParams = {
        search: searchParam || '',
        category: categoryParam || '',
        priority: priorityParam || '',
        pinned: pinnedParam || '',
        page: pageParam,
        limit: limitParam
      }

      console.log('📡 NOTES API CALL PARAMS:', apiParams)

      const response = await notesService.getAllNotes(apiParams)

      console.log('✅ NOTES API RESPONSE:', response)

      setNotes(response.data || [])
      setPagination(response.pagination || {})
      setPriorityCounts(response.priorityCounts || [])

      // อัพเดท categories ถ้ามี
      if (response.categories) {
        setCategories(response.categories)
      }

      if (response.data && response.data.length === 0) {
        console.log('ℹ️ No notes found with current filters')
      }

    } catch (error) {
      console.error('❌ Fetch notes error:', error)
      message.error(`ไม่สามารถโหลดข้อมูลโน๊ตได้: ${error.message || 'เกิดข้อผิดพลาด'}`)
      setNotes([])
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await notesService.getCategories()
      setCategories(response.data || [])
      setDefaultCategories(response.defaultCategories || defaultCategoriesList)
    } catch (error) {
      console.error('Error fetching categories:', error)
      setDefaultCategories(defaultCategoriesList)
    }
  }

  // *** แก้ไข Search Input Problem - แยก Input State และ Search State ***
  const handleSearch = (value) => {
    console.log('🔍 Handle Search:', value)
    setSearchTerm(value) // อัปเดต search term สำหรับ API
    setSearchInput(value) // sync input display
    const newPagination = { ...pagination, page: 1 }
    setPagination(newPagination)
    // ใช้การเรียกตรงแทน setTimeout เพื่อป้องกัน race condition
    fetchNotes({
      search: value,
      category: selectedCategory,
      priority: selectedPriority,
      pinned: showPinnedOnly,
      page: 1
    })
  }

  // Handle input change สำหรับการพิมพ์ (ไม่เรียก API)
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value) // อัปเดตเฉพาะ input display
  }

  const handleCategoryFilter = (category) => {
    console.log('📂 Handle Category Filter:', category)
    setSelectedCategory(category)
    const newPagination = { ...pagination, page: 1 }
    setPagination(newPagination)
    // ส่งค่าใหม่โดยตรงแทนการอาศัย state ที่อาจยังไม่อัปเดต
    fetchNotes({
      search: searchTerm,
      category: category, // ใช้ค่าใหม่ที่ส่งเข้ามา
      priority: selectedPriority,
      pinned: showPinnedOnly,
      page: 1
    })
  }

  const handlePriorityFilter = (priority) => {
    console.log('⭐ Handle Priority Filter:', priority)
    setSelectedPriority(priority)
    const newPagination = { ...pagination, page: 1 }
    setPagination(newPagination)
    fetchNotes({
      search: searchTerm,
      category: selectedCategory,
      priority: priority, // ใช้ค่าใหม่ที่ส่งเข้ามา
      pinned: showPinnedOnly,
      page: 1
    })
  }

  const handlePinnedFilter = (pinned) => {
    console.log('📌 Handle Pinned Filter:', pinned)
    setShowPinnedOnly(pinned)
    const newPagination = { ...pagination, page: 1 }
    setPagination(newPagination)
    fetchNotes({
      search: searchTerm,
      category: selectedCategory,
      priority: selectedPriority,
      pinned: pinned, // ใช้ค่าใหม่ที่ส่งเข้ามา
      page: 1
    })
  }

  const handlePageChange = (page, pageSize) => {
    const newPagination = { ...pagination, page, limit: pageSize }
    setPagination(newPagination)
    fetchNotes({ page, limit: pageSize })
  }

  const clearAllFilters = () => {
    console.log('🧹 Clearing all filters')
    setSearchTerm('')
    setSearchInput('') // clear input display ด้วย
    setSelectedCategory('')
    setSelectedPriority('')
    setShowPinnedOnly('')
    const newPagination = { ...pagination, page: 1 }
    setPagination(newPagination)
    // เรียกตรงเพื่อป้องกัน race condition
    fetchNotes({ search: '', category: '', priority: '', pinned: '', page: 1 })
  }

  // CRUD Operations
  const handleAddNote = () => {
    setEditingNote(null)
    setModalVisible(true)
    form.resetFields()
    form.setFieldsValue({
      priority: 'medium',
      category: 'general',
      is_pinned: false,
      tags: []
    })
  }

  const handleEditNote = (note) => {
    setEditingNote(note)
    setModalVisible(true)
    form.setFieldsValue({
      title: note.title,
      content: note.content,
      category: note.category,
      priority: note.priority,
      is_pinned: note.is_pinned,
      tags: note.tags || []
    })
  }

  const handleViewNote = (note) => {
    setViewingNote(note)
    setViewModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()

      const noteData = {
        ...values,
        tags: values.tags || []
      }

      if (editingNote) {
        await notesService.updateNote(editingNote.id, noteData)
        message.success('อัปเดตโน๊ตสำเร็จ')
      } else {
        await notesService.createNote(noteData)
        message.success('เพิ่มโน๊ตสำเร็จ')
      }

      setModalVisible(false)
      form.resetFields()
      fetchNotes({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
      fetchCategories() // อัพเดท categories
    } catch (error) {
      console.error('Error saving note:', error)
      if (error.errors) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else {
        message.error(error.message || 'ไม่สามารถบันทึกโน๊ตได้')
      }
    }
  }

  const handleDeleteNote = async (id) => {
    try {
      await notesService.deleteNote(id)
      message.success('ลบโน๊ตสำเร็จ')
      fetchNotes({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
      fetchCategories()
    } catch (error) {
      console.error('Error deleting note:', error)
      message.error('ไม่สามารถลบโน๊ตได้')
    }
  }

  const handleTogglePin = async (id, currentPinStatus) => {
    try {
      await notesService.togglePin(id)
      message.success(currentPinStatus ? 'ยกเลิกปักหมุดแล้ว' : 'ปักหมุดแล้ว')
      fetchNotes({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      console.error('Error toggling pin:', error)
      message.error('ไม่สามารถเปลี่ยนสถานะปักหมุดได้')
    }
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    setViewModalVisible(false)
    form.resetFields()
  }

  // Get priority color
  const getPriorityTag = (priority) => {
    const config = priorityConfig[priority] || priorityConfig.medium
    return (
      <Tag color={config.color} size="small">
        {config.icon} {config.label}
      </Tag>
    )
  }

  // Render single note card
  const NoteCard = ({ note }) => (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        border: note.is_pinned ? '2px solid #faad14' : '1px solid #d9d9d9'
      }}
      hoverable
      actions={[
        <Tooltip title="ดูรายละเอียด">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewNote(note)}
            size="small"
          />
        </Tooltip>,
        <Tooltip title={note.is_pinned ? 'ยกเลิกปักหมุด' : 'ปักหมุด'}>
          <Button
            type="text"
            icon={note.is_pinned ? <PushpinFilled style={{ color: '#faad14' }} /> : <PushpinOutlined />}
            onClick={() => handleTogglePin(note.id, note.is_pinned)}
            size="small"
          />
        </Tooltip>,
        <Tooltip title="แก้ไข">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditNote(note)}
            size="small"
          />
        </Tooltip>,
        <Popconfirm
          title="ยืนยันการลบโน๊ตนี้?"
          onConfirm={() => handleDeleteNote(note.id)}
          okText="ลบ"
          cancelText="ยกเลิก"
        >
          <Tooltip title="ลบ">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              size="small"
            />
          </Tooltip>
        </Popconfirm>
      ]}
    >
      <div style={{ position: 'relative' }}>
        {note.is_pinned && (
          <PushpinFilled
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              color: '#faad14',
              fontSize: '16px'
            }}
          />
        )}

        <div style={{ marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
            {note.title}
          </Title>
          <Space size="small">
            <Tag color="green" size="small">{note.category}</Tag>
            {getPriorityTag(note.priority)}
          </Space>
        </div>

        <Paragraph
          ellipsis={{ rows: 3, expandable: false }}
          style={{ marginBottom: 8, color: '#666' }}
        >
          {note.content}
        </Paragraph>

        {note.tags && note.tags.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            {note.tags.map((tag, index) => (
              <Tag key={index} size="small" icon={<TagOutlined />}>
                {tag}
              </Tag>
            ))}
          </div>
        )}

        <div style={{ fontSize: '12px', color: '#999' }}>
          <Space split={<span>•</span>}>
            <span><UserOutlined /> {note.created_by_name}</span>
            <span><ClockCircleOutlined /> {dayjs(note.updated_at).format('DD/MM/YYYY HH:mm')}</span>
          </Space>
        </div>
      </div>
    </Card>
  )

  // Render filters component
  const FiltersComponent = () => (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Search
            key={searchKey}
            defaultValue={searchTerm}     // ใช้ defaultValue แทน value
            onSearch={handleSearch}       // เรียก API เฉพาะตอนกด Enter/Search
            onClear={() => handleSearch('')}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>หมวดหมู่:</Text>
          <Select
            value={selectedCategory}
            onChange={handleCategoryFilter}
            style={{ width: '100%' }}
            size="small"
            placeholder="เลือกหมวดหมู่"
            allowClear
          >
            {[...defaultCategories, ...categories.map(c => c.category || c)]
              .filter((value, index, self) => self.indexOf(value) === index)
              .map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))
            }
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>ระดับความสำคัญ:</Text>
          <Select
            value={selectedPriority}
            onChange={handlePriorityFilter}
            style={{ width: '100%' }}
            size="small"
            placeholder="เลือกระดับ"
            allowClear
          >
            <Option value="low">🔵 ต่ำ</Option>
            <Option value="medium">🟡 ปานกลาง</Option>
            <Option value="high">🔴 สูง</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>สถานะปักหมุด:</Text>
          <Select
            value={showPinnedOnly}
            onChange={handlePinnedFilter}
            style={{ width: '100%' }}
            size="small"
            placeholder="ทั้งหมด"
            allowClear
          >
            <Option value="true">โน๊ตที่ปักหมุด</Option>
            <Option value="false">โน๊ตทั่วไป</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Button
            size="small"
            onClick={clearAllFilters}
            style={{ width: '100%', marginTop: 16 }}
          >
            ล้างตัวกรอง
          </Button>
        </Col>
      </Row>
    </Card>
  )

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            <BookOutlined style={{ marginRight: 12, color: '#1890ff' }} />
            โน๊ตบันทึกช่วยจำ
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersVisible(!filtersVisible)}
              type={filtersVisible ? 'primary' : 'default'}
              size={isMobile ? 'small' : 'default'}
            >
              {isMobile ? '' : 'ตัวกรอง'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNote}
              size={isMobile ? 'small' : 'default'}
            >
              {isMobile ? '' : 'เพิ่มโน๊ต'}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      {filtersVisible && <FiltersComponent />}

      {/* Notes Grid */}
      <Spin spinning={loading}>
        {notes.length > 0 ? (
          <>
            <Row gutter={[16, 16]}>
              {notes.map((note) => (
                <Col
                  key={note.id}
                  xs={24}
                  sm={12}
                  md={8}
                  lg={6}
                  xl={6}
                >
                  <NoteCard note={note} />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Row justify="center" style={{ marginTop: 24 }}>
                <Pagination
                  current={pagination.page}
                  total={pagination.total}
                  pageSize={pagination.limit}
                  onChange={handlePageChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} จาก ${total} โน๊ต`
                  }
                  pageSizeOptions={['12', '24', '36', '48']}
                />
              </Row>
            )}
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="ไม่มีโน๊ตบันทึก"
            style={{ marginTop: 48 }}
          >
            <Button type="primary" onClick={handleAddNote}>
              เพิ่มโน๊ตแรก
            </Button>
          </Empty>
        )}
      </Spin>

      {/* Float Button for mobile */}
      {isMobile && (
        <FloatButton
          icon={<PlusOutlined />}
          type="primary"
          style={{ right: 24, bottom: 24 }}
          onClick={handleAddNote}
        />
      )}

      {/* Add/Edit Note Modal */}
      <Modal
        title={editingNote ? 'แก้ไขโน๊ต' : 'เพิ่มโน๊ตใหม่'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={isMobile ? '95%' : 800}
        okText="บันทึก"
        cancelText="ยกเลิก"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="หัวข้อ"
            rules={[{ required: true, message: 'กรุณากรอกหัวข้อ' }]}
          >
            <Input placeholder="หัวข้อโน๊ต" />
          </Form.Item>

          <Form.Item
            name="content"
            label="เนื้อหา"
            rules={[{ required: true, message: 'กรุณากรอกเนื้อหา' }]}
          >
            <TextArea
              rows={8}
              placeholder="เนื้อหาโน๊ต"
              showCount
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="category" label="หมวดหมู่">
                <Select placeholder="เลือกหมวดหมู่">
                  {[...defaultCategories, ...categories.map(c => c.category || c)]
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .map((category) => (
                      <Option key={category} value={category}>
                        {category}
                      </Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="priority" label="ระดับความสำคัญ">
                <Select placeholder="เลือกระดับ">
                  <Option value="low">🔵 ต่ำ</Option>
                  <Option value="medium">🟡 ปานกลาง</Option>
                  <Option value="high">🔴 สูง</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="tags" label="แท็ก">
            <Select
              mode="tags"
              placeholder="เพิ่มแท็ก (กด Enter เพื่อเพิ่ม)"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item name="is_pinned" valuePropName="checked">
            <Space>
              <PushpinOutlined />
              <span>ปักหมุดโน๊ตนี้</span>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Note Modal */}
      <Modal
        title="รายละเอียดโน๊ต"
        open={viewModalVisible}
        onCancel={handleModalCancel}
        footer={[
          <Button key="close" onClick={handleModalCancel}>
            ปิด
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setViewModalVisible(false)
              handleEditNote(viewingNote)
            }}
          >
            แก้ไข
          </Button>
        ]}
        width={isMobile ? '95%' : 700}
      >
        {viewingNote && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Title level={4}>{viewingNote.title}</Title>
              <Space>
                <Tag color="green">{viewingNote.category}</Tag>
                {getPriorityTag(viewingNote.priority)}
                {viewingNote.is_pinned && (
                  <Tag color="gold" icon={<PushpinFilled />}>
                    ปักหมุด
                  </Tag>
                )}
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <Text style={{ whiteSpace: 'pre-line' }}>
                {viewingNote.content}
              </Text>
            </div>

            {viewingNote.tags && viewingNote.tags.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>แท็ก: </Text>
                {viewingNote.tags.map((tag, index) => (
                  <Tag key={index} icon={<TagOutlined />}>
                    {tag}
                  </Tag>
                ))}
              </div>
            )}

            <Divider />

            <div style={{ fontSize: '12px', color: '#999' }}>
              <Space direction="vertical" size="small">
                <span><UserOutlined /> ผู้สร้าง: {viewingNote.created_by_name}</span>
                <span><ClockCircleOutlined /> สร้างเมื่อ: {dayjs(viewingNote.created_at).format('DD/MM/YYYY')}</span>
                <span><ClockCircleOutlined /> แก้ไขล่าสุด: {dayjs(viewingNote.updated_at).format('DD/MM/YYYY')}</span>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default NotesPage